import {AnimatePresence, motion, MotionStyle} from 'framer-motion';
import {Box, Heading, Hide, HStack, Show, Space, ThemeZIndexPath} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

// React's KeyboardEvent is aliased so it does not shadow the DOM KeyboardEvent
// used by the window keydown listener in registerEscapeKeydown
import {KeyboardEvent as ReactKeyboardEvent, MouseEvent, ReactNode, useEffect, useRef} from 'react';

import {isNotNil, not} from 'ramda';

import {
  findFirstFocusableElement,
  RequiredTestIdProps,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {ButtonProps} from '../Button/Button';
import {ButtonGroup} from '../ButtonGroup/ButtonGroup';
import {IconButton} from '../IconButton/IconButton';
import {Pagination, PaginationProps} from '../Pagination/Pagination';
import {Portal} from '../Portal/components/Portal';
import {DialogTransition} from './components/DialogTransition';
import {DialogSize} from './types/DialogSize';
import {ScrollBehavior} from './types/ScrollBehavior';

const DEFAULT_SCROLL_BEHAVIOR = 'inside';
const DIALOG_CLOSE_BUTTON_SELECTOR = '[data-testid*="dialogCloseButton"]';

type DialogPaginationProps = Pick<PaginationProps, 'pagesQuantity' | 'onPageChange' | 'page'>;

export interface DialogProps extends TestIdProps {
  isOpen: boolean;
  children: ReactNode;
  title?: string;
  scrollBehavior?: ScrollBehavior;
  size?: DialogSize;
  withAdditionalFooter?: boolean;
  id?: string;
  disableBodyPadding?: boolean;
  disableMaxHeight?: boolean;
  hideCloseButton?: boolean;
  /**
   * @about Displays sticky footer with buttons inside
   */
  buttons?: ButtonProps[];
  onClose?: () => void;
  onCloseComplete?: () => void;
  /**
   * If `true`, clicking the overlay outside the dialog panel calls `onClose`. Opt-in so that
   * existing dialogs keep requiring an explicit close; dialogs whose unsaved input must not be
   * lost to a stray click should stay opted out.
   * @default false
   */
  closeOnOverlayClick?: boolean;
  /**
   * If `true`, the modal will return focus to the element that triggered it when it closes.
   * @default true
   */
  returnFocusOnClose?: boolean;
  /**
   * @about Focuses the element whose data-testid contains this value when the
   * dialog opens, instead of the default close button. Used e.g. by
   * confirmation dialogs to focus their confirm button.
   */
  initialFocusTestId?: string;
  pagination?: DialogPaginationProps | ReactNode;
  /**
   * @about overrides z-index of the dialog so that it can be displayed above the lightbox
   */
  isInLightbox?: boolean;
}

const DIALOG_HEIGHT_BREAKPOINT_PX = 1000;
export function Dialog(props: DialogProps): ReactNode {
  const theme = useTheme();
  const isEnlarged = window.innerHeight <= DIALOG_HEIGHT_BREAKPOINT_PX;
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  // Guards overlay-click closing against text selections that start inside the panel and end
  // on the overlay — such a drag fires a click on the overlay even though the user never
  // "clicked outside".
  const isOverlayMouseDownRef = useRef(false);

  const handleCloseButtonClick = () => {
    props.onClose?.();
  };

  const handleOverlayMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    isOverlayMouseDownRef.current = event.target === event.currentTarget;
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (
      props.closeOnOverlayClick &&
      isOverlayMouseDownRef.current &&
      event.target === event.currentTarget
    ) {
      props.onClose?.();
    }
  };

  const handleContentKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    // When the dialog focuses a button on open (initialFocusTestId), a chord
    // like Ctrl/⌘+Enter that opened the dialog and is still held would OS
    // auto-repeat Enter onto the focused button and activate it. Only a fresh
    // Enter press (keyup first) may confirm — auto-repeats are swallowed.
    if (isNotNil(props.initialFocusTestId) && event.key === 'Enter' && event.repeat) {
      event.preventDefault();
    }
  };

  const isOpen = props.isOpen;
  const onClose = props.onClose;

  useEffect(
    function registerEscapeKeydown() {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          event.stopPropagation();

          onClose?.();
        }
      };

      window.addEventListener('keydown', onKeyDown);

      return () => {
        window.removeEventListener('keydown', onKeyDown);
      };
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (props.isOpen) {
      // Store the previously focused element before opening the modal. Guarded
      // so a re-run while already open (e.g. initialFocusTestId identity change)
      // cannot overwrite it with an element inside the dialog, which would
      // break focus restoration on close.
      if (previouslyFocusedElementRef.current === null) {
        previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      }

      // Use setTimeout because when this effect runs, the dialog content
      // hasn't been fully rendered yet, so findFirstFocusableElement would return null
      setTimeout(() => {
        // Caller-requested initial focus target wins over the close button
        const initialFocusElement = props.initialFocusTestId
          ? dialogContentRef.current?.querySelector<HTMLButtonElement>(
              `[data-testid*="${props.initialFocusTestId}"]`
            )
          : null;

        if (initialFocusElement) {
          initialFocusElement.focus();
          return;
        }

        // Try to focus the close button first if it exists
        const closeButton = dialogContentRef.current?.querySelector<HTMLButtonElement>(
          DIALOG_CLOSE_BUTTON_SELECTOR
        );

        if (closeButton) {
          closeButton.focus();
        } else {
          // Fallback to first focusable element if close button is hidden
          const firstFocusableElement = findFirstFocusableElement(dialogContentRef.current);
          if (firstFocusableElement) {
            firstFocusableElement.focus();
          }
        }
      }, 0);
    } else {
      // Restore focus to the previously focused element when modal closes
      if (props.returnFocusOnClose !== false && previouslyFocusedElementRef.current) {
        previouslyFocusedElementRef.current.focus();
      }

      // Always clear so the next open stores a fresh trigger element
      previouslyFocusedElementRef.current = null;
    }
  }, [props.isOpen, props.returnFocusOnClose, props.initialFocusTestId]);

  const scrollBehavior = props.scrollBehavior ?? DEFAULT_SCROLL_BEHAVIOR;

  return (
    <AnimatePresence onExitComplete={props.onCloseComplete}>
      {props.isOpen && (
        <Portal>
          <StyledOverlay
            zIndex={props.isInLightbox ? 'LIGHTBOX_MODAL' : 'MODAL'}
            data-testid={suffixTestId('dialogOverlay', props)}
          >
            <div
              role="dialog"
              ref={dialogContentRef}
              onMouseDown={handleOverlayMouseDown}
              onClick={handleOverlayClick}
              onKeyDown={handleContentKeyDown}
              data-testid={suffixTestId('dialogContent', props)}
              css={css`
                display: flex;
                width: 100vw;
                height: 100%;
                align-items: center;
                justify-content: center;
                position: fixed;
                left: 0;
                top: 0;
                z-index: ${theme.zIndices[props.isInLightbox ? 'LIGHTBOX_MODAL' : 'MODAL']};
                overflow: ${scrollBehavior === 'inside' ? 'hidden' : 'auto'};
                padding: ${scrollBehavior === 'outside' ? `${theme.getSize(8)} 0` : '0'};
              `}
            >
              <DialogTransition
                size={props.size}
                isEnlarged={isEnlarged}
                scrollBehavior={scrollBehavior}
                disableMaxHeight={props.disableMaxHeight}
              >
                <Show when={props.title}>
                  <div
                    css={css`
                      display: block;
                      flex: 0 0 auto;
                      box-sizing: border-box;
                      padding: 16px;
                      border-bottom: 1px solid;
                      border-color: ${({theme}) => theme.colors.general.separator};
                    `}
                    data-testid={suffixTestId('dialogHeader', props)}
                  >
                    <HStack spacing={4} justify="space-between" align="center">
                      <Heading size={4}>{props.title}</Heading>
                      <HStack>
                        {typeof props.pagination === 'object' &&
                        isNotNil((props.pagination as DialogPaginationProps).pagesQuantity) ? (
                          <Pagination
                            {...(props.pagination as DialogPaginationProps)}
                            variant="text-only"
                            showPrevButton
                            showNextButton
                            data-testid={suffixTestId('dialogHeaderPagination', props)}
                          />
                        ) : (
                          (props.pagination as ReactNode)
                        )}
                        <Space horizontal={2} />
                        <Hide when={props.hideCloseButton}>
                          <IconButton
                            data-testid={suffixTestId('dialogCloseButton', props)}
                            icon="navigation/close"
                            aria-label="closeDialog"
                            onClick={handleCloseButtonClick}
                          />
                        </Hide>
                      </HStack>
                    </HStack>
                  </div>
                </Show>

                <div
                  css={css`
                    display: block;
                    flex: ${scrollBehavior === 'inside' ? '1 1 auto' : '0 0 auto'};
                    margin-bottom: ${props.withAdditionalFooter ? theme.getSize(16) : 0};
                    padding: ${props.disableBodyPadding ? 0 : theme.getSize(4)};
                    overflow: ${scrollBehavior === 'inside' ? 'auto' : undefined};
                    ${scrollBehavior === 'outside' && `max-height: unset;`}
                  `}
                  data-testid={suffixTestId('dialogBody', props)}
                >
                  {props.children}
                </div>

                <Show when={props.buttons && not(props.withAdditionalFooter)}>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: flex-end;
                      padding: 0;
                    `}
                    data-testid={suffixTestId('dialogFooter', props)}
                  >
                    <Box padding={4}>
                      <ButtonGroup buttons={props.buttons} />
                    </Box>
                  </div>
                </Show>
              </DialogTransition>
            </div>
          </StyledOverlay>
        </Portal>
      )}
    </AnimatePresence>
  );
}

interface StylesOverlayProps extends RequiredTestIdProps {
  zIndex: ThemeZIndexPath;
  children: ReactNode;
}

const StyledOverlay = (props: StylesOverlayProps) => {
  const theme = useTheme();

  return (
    <motion.div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: theme.zIndices[props.zIndex] as MotionStyle['zIndex'],
        backgroundColor: theme.colors.palettes.neutral[900][20],
        overflow: 'auto',
      }}
      initial="hidden"
      animate="visible"
      exit="hidden"
      data-testid={props['data-testid']}
      variants={{
        visible: {
          opacity: 1,
          transition: {
            duration: 0.3,
            ease: [0.3, 0.1, 0.75, 0.9],
          },
        },
        hidden: {
          opacity: 0,
          transition: {
            duration: 0.1,
            ease: [0.9, 0.75, 0.1, 0.3],
          },
        },
      }}
    >
      {props.children}
    </motion.div>
  );
};
